import React, { useEffect, useMemo, useRef, useState } from 'react';

/*
  Advanced Select (accessible, keyboard-friendly)
  - Fully controlled via value/onChange
  - options: [{ value, label }]
  - placeholder: string
  - disabled: boolean
  - className: extra classes
  - Matches branding via CSS in ui/styles/components/forms/Select.css
*/

const Select = ({
  value,
  onChange,
  options = [],
  placeholder = 'Seleccionar',
  disabled = false,
  className = '',
  name,
  error = false,
}) => {
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const containerRef = useRef(null);
  const listRef = useRef(null);

  const selectedOption = useMemo(
    () => options.find((opt) => opt.value === value) || null,
    [options, value]
  );

  const close = () => {
    setOpen(false);
    setHighlightIndex(-1);
  };

  const commitSelection = (opt) => {
    if (!opt) return;
    if (onChange) onChange({ target: { name, value: opt.value } });
    close();
  };

  // Click outside to close
  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        close();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Keyboard interactions
  const onKeyDown = (e) => {
    if (disabled) return;
    switch (e.key) {
      case ' ': // space
      case 'Enter':
        e.preventDefault();
        if (!open) {
          setOpen(true);
          setHighlightIndex(Math.max(0, options.findIndex((o) => o.value === value)));
        } else if (highlightIndex >= 0) {
          commitSelection(options[highlightIndex]);
        }
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!open) {
          setOpen(true);
          setHighlightIndex(0);
        } else {
          setHighlightIndex((i) => Math.min(options.length - 1, (i < 0 ? 0 : i + 1)));
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!open) {
          setOpen(true);
          setHighlightIndex(options.length - 1);
        } else {
          setHighlightIndex((i) => Math.max(0, (i < 0 ? options.length - 1 : i - 1)));
        }
        break;
      case 'Escape':
        if (open) {
          e.preventDefault();
          close();
        }
        break;
      default:
        break;
    }
  };

  // Scroll highlighted into view
  useEffect(() => {
    if (!open) return;
    const list = listRef.current;
    if (!list || highlightIndex < 0) return;
    const item = list.children[highlightIndex];
    if (item && item.scrollIntoView) {
      item.scrollIntoView({ block: 'nearest' });
    }
  }, [open, highlightIndex]);

  return (
    <div
      ref={containerRef}
      className={`jf-select ${disabled ? 'is-disabled' : ''} ${error ? 'is-error' : ''} ${className}`.trim()}
      tabIndex={disabled ? -1 : 0}
      role="combobox"
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-disabled={disabled}
      onKeyDown={onKeyDown}
      onClick={() => !disabled && setOpen((o) => !o)}
    >
      <input type="hidden" name={name} value={value || ''} readOnly />
      <div className="jf-select__control">
        <div className={`jf-select__value ${!selectedOption ? 'is-placeholder' : ''}`}>
          {selectedOption ? selectedOption.label : placeholder}
        </div>
        <div className="jf-select__indicator" aria-hidden>
          <span className="jf-select__chevron" />
        </div>
      </div>
      {open && (
        <div className="jf-select__menu" role="listbox">
          <ul ref={listRef} className="jf-select__list">
            {options.map((opt, idx) => {
              const isSelected = value === opt.value;
              const isHighlighted = idx === highlightIndex;
              return (
                <li
                  key={opt.value}
                  role="option"
                  aria-selected={isSelected}
                  className={`jf-select__option ${isSelected ? 'is-selected' : ''} ${isHighlighted ? 'is-highlighted' : ''}`}
                  onMouseEnter={() => setHighlightIndex(idx)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => commitSelection(opt)}
                >
                  {opt.label}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default Select;


